import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../login-validation.component/login-validation.component';

import { AlertService, AlertType } from '../../shared/services/alert.service';

import { BookCoverStruct, BookCoverStyleStruct } from '../../shared/services/structs/book-cover.struct';
import { BookCoverStyleRequest } from '../../shared/services/requests/platform/book-cover-style.request';
import { BookCoverStyleResponse } from '../../shared/services/responses/platform/book-cover-style.response';
import { BookCoverStyleService } from '../../shared/services/apis/platform/book-cover-style.service';

import { HireDesignModalComponent } from '../hire-design-modal/hire-design-modal.component';
import { ReturnStruct } from '../../shared/services/structs/return.struct';

import { NgZone } from '@angular/core';

import { FileUploadService } from '../../shared/services/apis/platform/file-upload.service';
import { FileUploadCreateResponse, FileUploadFinalizeResponse } from '../../shared/services/responses/platform/file-upload-create.response';
import { UploadPurposeEnum } from '../../shared/services/enums/upload-purpose.enum';

@Component({
  selector: 'app-book-cover-style',
  standalone: true,
  templateUrl: './book-cover-style.component.html',
  styleUrls: ['./book-cover-style.component.css'],
  imports: [
    CommonModule,
    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ]
})
export class BookCoverStyleComponent implements OnInit {
  idBookVersion!: number;

  isLoading = false;
  isSaving = false;

  listBookCover: BookCoverStruct[] = [];

  selectedCoverIndex = 0;
  selectedStyleIndex = -1;

  coverService = false; // contratação de designer

  // Upload PDF
  uploadedPdfLocalName = '';
  uploadedPdfSizeBytes = 0;
  uploadedPdfBlobUrl = ''; // preview local (abre em nova aba)
  uploadedPdfServerFileName = ''; // <-- ESTE é o "name" que você quer salvar

  expandedImage?: string;


  public req = new BookCoverStyleRequest();
  // Ajuste se quiser impor limite
  private static readonly MAX_PDF_BYTES = 210 * 1024 * 1024;

  @ViewChild('coverTrack') coverTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('styleTrack') styleTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private srv: BookCoverStyleService,
    private alert: AlertService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private fileUpload: FileUploadService
  ) { }

  ngOnInit(): void {
    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;

    if (!this.idBookVersion) {
      this.alert.show('Erro', 'Livro inválido na rota.', AlertType.error);
      return;
    }

    this.load();
  }
  private ensurePdfLimit(file: File): boolean {
    if (file.size <= BookCoverStyleComponent.MAX_PDF_BYTES) return true;

    this.alert.show(
      'PDF muito grande',
      `Arquivo com ${(file.size / (1024 * 1024)).toFixed(1)} MB. Limite: ~210 MB.`,
      AlertType.warning
    );
    return false;
  }


  load(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.srv.GetAll(this.idBookVersion).subscribe({
      next: (res: BookCoverStyleResponse) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription, AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        // lista
        this.listBookCover = res.listBookCoverStyle ?? [];

        // aplica seleção persistida (capa/estilo)
        this.applyPersistedSelection(res);

        // aplica upload persistido (pdf já enviado)
        this.applyPersistedUpload(res);

        // scroll para o item selecionado
        setTimeout(() => {
          this.scrollToCard(this.coverTrack, this.selectedCoverIndex);

          // se tem estilo selecionado, scroll nele, senão vai pro começo
          const styleIdx = this.selectedStyleIndex >= 0 ? this.selectedStyleIndex : 0;
          this.scrollToCard(this.styleTrack, styleIdx);
        }, 0);

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.alert.show('Erro inesperado', err, AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }


  private async uploadByPurpose(file: File, purpose: UploadPurposeEnum): Promise<FileUploadFinalizeResponse> {
    // 1) Create
    const createRes = await new Promise<FileUploadCreateResponse>((resolve, reject) => {
      this.fileUpload.create({
        originalFileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        purpose: purpose
      }).subscribe({
        next: (x: any) => resolve(x as FileUploadCreateResponse),
        error: (e) => reject(e)
      });
    });

    if (!createRes?.idFileUploadSession || !createRes?.uploadUrl) {
      throw new Error('CreateUpload inválido (sem uploadId/url).');
    }

    // 2) PUT
    const headers = new Headers();

    const extraHeaders: Record<string, string> | null | undefined =
      (createRes as any).headers || createRes.headers || null;

    if (extraHeaders) {
      for (const k of Object.keys(extraHeaders)) {
        const v = extraHeaders[k];
        if (v != null) headers.set(k, String(v));
      }
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', file.type || 'application/octet-stream');
    }

    const putResp = await fetch(createRes.uploadUrl, {
      method: 'PUT',
      headers,
      body: file
    });

    if (!putResp.ok) {
      const msg = await putResp.text().catch(() => '');
      throw new Error(`Falha no PUT (${putResp.status}). ${msg || ''}`.trim());
    }

    // 3) Finalize
    const fin = await new Promise<FileUploadFinalizeResponse>((resolve, reject) => {
      this.fileUpload.finalize({ idFileUploadSession: createRes.idFileUploadSession }).subscribe({
        next: (x) => resolve(x as FileUploadFinalizeResponse),
        error: (e) => reject(e)
      });
    });

    if (!fin?.fileName) {
      throw new Error('FinalizeUpload inválido (sem fileName).');
    }

    return fin;
  }

  get selectedCover(): BookCoverStruct | null {
    if (!this.listBookCover?.length) return null;
    return this.listBookCover[this.selectedCoverIndex] ?? null;
  }

  get stylesForSelectedCover(): BookCoverStyleStruct[] {
    return this.selectedCover?.listBookCoverStyles ?? [];
  }

  get selectedStyle(): BookCoverStyleStruct | null {
    const styles = this.stylesForSelectedCover;
    if (!styles.length) return null;
    if (this.selectedStyleIndex < 0) return null;
    return styles[this.selectedStyleIndex] ?? null;
  }

  private resolveFileNameFromUrl(url: string, fallback: string): string {
    try {
      const clean = (url || '').split('?')[0];
      const parts = clean.split('/').filter(Boolean);
      const last = parts[parts.length - 1] || fallback;
      return decodeURIComponent(last);
    } catch {
      return fallback;
    }
  }

  private findCoverIndexByIdBookCover(idBookCover: number): number {
    if (!idBookCover) return 0;
    const idx = (this.listBookCover || []).findIndex(c => Number((c as any)?.idBookCover) === Number(idBookCover));
    return idx >= 0 ? idx : 0;
  }

  private findCoverAndStyleIndexByStyleId(idBookCoverStyle: number): { coverIndex: number; styleIndex: number } {
    if (!idBookCoverStyle) return { coverIndex: 0, styleIndex: -1 };

    for (let i = 0; i < (this.listBookCover || []).length; i++) {
      const styles = this.listBookCover[i]?.listBookCoverStyles ?? [];
      const j = styles.findIndex(s => Number(s?.idBookCoverStyle) === Number(idBookCoverStyle));
      if (j >= 0) return { coverIndex: i, styleIndex: j };
    }

    return { coverIndex: 0, styleIndex: -1 };
  }

  private applyPersistedSelection(res: BookCoverStyleResponse): void {
    // 1) Prioridade: idBookCoverStyle (mais preciso)
    const persistedStyleId = Number(res?.idBookCoverStyle || 0);
    const persistedCoverId = Number(res?.idBookCover || 0);

    if (persistedStyleId > 0) {
      const found = this.findCoverAndStyleIndexByStyleId(persistedStyleId);
      this.selectedCoverIndex = found.coverIndex;
      this.selectedStyleIndex = found.styleIndex;
      return;
    }

    // 2) Fallback: idBookCover
    if (persistedCoverId > 0) {
      this.selectedCoverIndex = this.findCoverIndexByIdBookCover(persistedCoverId);
      this.selectedStyleIndex = -1;

      // se só existir 1 estilo, auto seleciona
      const styles = this.stylesForSelectedCover;
      if (styles.length === 1) this.selectedStyleIndex = 0;
      return;
    }

    // 3) Default atual
    this.selectedCoverIndex = 0;
    this.selectedStyleIndex = -1;

    const styles = this.stylesForSelectedCover;
    if (styles.length === 1) this.selectedStyleIndex = 0;
  }

  private applyPersistedUpload(res: BookCoverStyleResponse): void {
    const url = String(res?.assembleCoverFileUrl || '').trim();
    if (!url) return;

    // limpa preview local antigo, e usa o url do servidor como "abrir"
    this.revokePdfUrl();

    const fileName = this.resolveFileNameFromUrl(url, 'capa.pdf');

    this.uploadedPdfLocalName = fileName;
    this.uploadedPdfServerFileName = fileName;

    // tamanho a API não devolve (fica 0)
    this.uploadedPdfSizeBytes = 0;

    // aqui a gente usa o link remoto como "preview"
    this.uploadedPdfBlobUrl = url;

    // e garante que o request já está pronto se o usuário só clicar em "Avançar"
    this.req.assembledFileName = fileName;
  }


  // ---------- Carrossel: Capas ----------
  selectCover(index: number): void {
    if (index < 0 || index >= this.listBookCover.length) return;

    this.selectedCoverIndex = index;
    this.selectedStyleIndex = -1;

    if (this.stylesForSelectedCover.length === 1) {
      this.selectedStyleIndex = 0;
    }

    setTimeout(() => this.scrollToCard(this.styleTrack, 0), 0);
    this.cdr.markForCheck();
  }

  scrollCovers(dir: 'left' | 'right') {
    this.scrollByViewport(this.coverTrack, dir);
  }

  // ---------- Carrossel: Estilos ----------
  selectStyle(index: number): void {
    const styles = this.stylesForSelectedCover;
    if (index < 0 || index >= styles.length) return;
    this.selectedStyleIndex = index;
    this.cdr.markForCheck();
  }

  scrollStyles(dir: 'left' | 'right') {
    this.scrollByViewport(this.styleTrack, dir);
  }

  private scrollByViewport(track: ElementRef<HTMLDivElement>, dir: 'left' | 'right') {
    if (!track?.nativeElement) return;
    const el = track.nativeElement;
    const delta = Math.max(260, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: dir === 'left' ? -delta : delta, behavior: 'smooth' });
  }

  private scrollToCard(track: ElementRef<HTMLDivElement>, index: number) {
    if (!track?.nativeElement) return;
    const el = track.nativeElement;
    const card = el.querySelector<HTMLElement>(`[data-idx="${index}"]`);
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  // ---------- Preview imagens (capas/estilos) ----------
  openImage(url: string) {
    this.expandedImage = url;
    this.cdr.markForCheck();
  }

  closeImage() {
    this.expandedImage = undefined;
    this.cdr.markForCheck();
  }

  // ---------- Upload PDF ----------
  onUploadClick(): void {
    this.fileInput?.nativeElement?.click();
  }

  async onFileSelected(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // PDF only
    const isPdfByType = file.type === 'application/pdf';
    const isPdfByName = (file.name || '').toLowerCase().endsWith('.pdf');
    if (!isPdfByType && !isPdfByName) {
      this.alert.show('Formato inválido', 'Envie apenas arquivo PDF.', AlertType.warning);
      input.value = '';
      return;
    }

    if (!this.ensurePdfLimit(file)) {
      input.value = '';
      return;
    }

    // UI state
    this.isSaving = true;
    this.uploadedPdfLocalName = file.name;
    this.uploadedPdfSizeBytes = file.size;

    this.revokePdfUrl();
    this.uploadedPdfBlobUrl = URL.createObjectURL(file);

    this.uploadedPdfServerFileName = ''; // limpa antes de enviar
    this.cdr.markForCheck();

    try {
      // >>> use o PURPOSE correto que você já tem no seu enum <<<
      // Se você ainda não tem um purpose específico para "capa montada", use o mais próximo
      const fin = await this.uploadByPurpose(file, UploadPurposeEnum.CoverForPrintPdf);

      // SALVA o "name" retornado
      this.zone.run(() => {
        this.uploadedPdfServerFileName = fin.fileName;

        this.req.assembledFileName = fin.fileName;

        this.alert.show('Sucesso', 'PDF enviado com sucesso.', AlertType.success);
        this.isSaving = false;
        this.cdr.markForCheck();
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar PDF.', AlertType.error);

        // limpa para não “parecer” que tem upload ok
        this.uploadedPdfLocalName = '';
        this.uploadedPdfSizeBytes = 0;
        this.uploadedPdfServerFileName = '';
        this.revokePdfUrl();

        this.isSaving = false;
        this.cdr.markForCheck();
      });
    } finally {
      input.value = '';
    }
  }


  openPdf(): void {
    if (!this.uploadedPdfBlobUrl) return;
    window.open(this.uploadedPdfBlobUrl, '_blank', 'noopener,noreferrer');
  }

  clearUpload(): void {
    this.uploadedPdfLocalName = '';
    this.uploadedPdfSizeBytes = 0;
    this.uploadedPdfServerFileName = '';
    this.req.assembledFileName = '';

    this.revokePdfUrl();
    if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
    this.cdr.markForCheck();
  }

  private revokePdfUrl(): void {
    if (this.uploadedPdfBlobUrl) {
      URL.revokeObjectURL(this.uploadedPdfBlobUrl);
      this.uploadedPdfBlobUrl = '';
    }
  }

  // ---------- Hire designer ----------
  onHireDesignerClick(): void {
    const dialogRef = this.dialog.open(HireDesignModalComponent, { data: this.idBookVersion });
    dialogRef.afterClosed().subscribe((contratou: boolean) => {
      this.coverService = !!contratou;
      this.cdr.markForCheck();
    });
  }

  // ---------- Ações principais ----------
  voltar(): void {
    this.router.navigate(['/novo-livro', this.idBookVersion]);
  }

  get canAdvance(): boolean {
    return !!this.selectedStyle || !!this.uploadedPdfServerFileName || !!this.req.assembledFileName || !!this.coverService;
  }

  get uploadedPdfSizeLabel(): string {
    if (!this.uploadedPdfSizeBytes) return '';
    const mb = this.uploadedPdfSizeBytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = this.uploadedPdfSizeBytes / 1024;
    return `${kb.toFixed(0)} KB`;
  }

  avancar(): void {
    // 1) estilo selecionado -> salva e segue
    if (this.selectedStyle || this.req.assembledFileName || this.coverService) {
      this.isSaving = true;
      this.cdr.markForCheck();

      this.req.idBookCoverStyle = this.selectedStyle?.idBookCoverStyle;
      this.req.idBookVersion = this.idBookVersion;
      this.req.coverService = this.coverService;

      this.srv.SetCoverStyle(this.req).subscribe({
        next: (res: ReturnStruct) => {
          if (res?.isError) {
            this.alert.show('Erro', res.errorDescription, AlertType.error);
            this.isSaving = false;
            this.cdr.markForCheck();
            return;
          }

          this.router.navigate(['/livro-impressao', this.idBookVersion]);

          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.alert.show('Erro', err, AlertType.error);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });

      return;
    }

    this.alert.show('Atenção', 'Selecione um estilo, faça upload (PDF) ou contrate um designer para continuar.', AlertType.warning);
  }
}
